import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import SiteNav from '@/components/Shared/SiteNav';
import FloatingNav from '@/components/Shared/FloatingNav';
import LandingPage from './pages/LandingPage';
import WorkRebuildPage from '@/src/pages/WorkRebuildPage';
import BlogPage from '@/src/pages/BlogPage';
import { resolveSwipeKey, type SwipeBaseRoute } from '@/lib/navigationRoutes';

type NavigationState = {
  backgroundLocation?: Location;
};

const renderSwipePage = (key: SwipeBaseRoute | '/') => {
  switch (key) {
    case '/work':
      return <WorkRebuildPage />;
    case '/blog':
      return <BlogPage />;
    default:
      return <LandingPage />;
  }
};

const SiteFrame: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const state = (location.state as NavigationState | undefined) ?? undefined;
  const baseLocation = (state?.backgroundLocation as Location | undefined) ?? location;
  const key = resolveSwipeKey(baseLocation.pathname);
  const isFirstRenderRef = useRef(true);
  const shouldSlide = key !== '/';
  const initialState = isFirstRenderRef.current
    ? false
    : shouldSlide
    ? { x: '100%', opacity: 0 }
    : { opacity: 0 };
  const animateState = shouldSlide ? { x: 0, opacity: 1 } : { opacity: 1 };
  const exitState = shouldSlide ? { x: '-100%', opacity: 0 } : { opacity: 0 };

  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  return (
    <>
      <div className="md:hidden">
        <FloatingNav forceVisible />
      </div>
      <div className="hidden md:block">
        <SiteNav />
      </div>
      <div className="relative z-0 overflow-x-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={key}
            initial={initialState}
            animate={animateState}
            exit={exitState}
            transition={{ duration: 0.65, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative z-0 min-h-screen"
            style={shouldSlide ? undefined : { transform: 'none' }}
          >
            {renderSwipePage(key)}
          </motion.div>
        </AnimatePresence>
      </div>
      {children}
    </>
  );
};

export default SiteFrame;
